import { css } from '@emotion/react';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import CopyToClipboardButton from 'components/CopyToClipboardButton/CopyToClipboardButton';
import ExpandableDetails from '../WorkflowRunDetailsPage/ExpandableDetails/ExpandableDetails';
import JsonViewer from '../WorkflowRunDetailsPage/JsonViewer/JsonViewer';

import bulkResetFailedTasks, { IFailedTasksFilter } from 'store/bulkResetFailedTasks';
import fetchWorkflowErrorSummary from 'store/fetchWorkflowErrorSummary';
import useWorkflowErrorSummaryAsync from 'store/useWorkflowErrorSummaryAsync';

import IRunEntry from 'types/IRunEntry';
import { IErrorVariant } from 'types/IWorkflowErrorSummary';

const preSx = {
  maxHeight: 300,
  overflow: 'auto',
  p: 1.5,
  bgcolor: 'action.hover',
  fontSize: '0.75rem',
  whiteSpace: 'pre-wrap',
  fontFamily: 'monospace',
} as const;

const taskCount = (count: number) => `${count} ${count === 1 ? 'task' : 'tasks'}`;

const summarySx = { '& .MuiAccordionSummary-content': { minWidth: 0, alignItems: 'center' } } as const;

function FailureSample({ sample }: { sample: IErrorVariant | Omit<IErrorVariant, 'message'> }) {
  return (
    <>
      {sample.sample_message && (
        <Box component="pre" sx={{ ...preSx, mb: sample.sample_traceback ? 1.5 : 0 }}>
          {sample.sample_message}
        </Box>
      )}
      {sample.sample_traceback && (
        <>
          <Typography component="div" variant="body2" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            Traceback
            <CopyToClipboardButton data={sample.sample_traceback} size="small" subject="traceback" />
          </Typography>
          <Box component="pre" sx={preSx}>
            {sample.sample_traceback}
          </Box>
        </>
      )}
      {!!sample.sample_task_ids?.length && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'inline-flex', alignItems: 'center', mt: 1 }}
        >
          Sample task ids: {sample.sample_task_ids.join(', ')}
          <CopyToClipboardButton data={sample.sample_task_ids.join('\n')} size="small" subject="task IDs" />
        </Typography>
      )}
    </>
  );
}

function ResetAllButton({
  resetting,
  disabled,
  onReset,
}: {
  resetting: boolean;
  disabled: boolean;
  onReset: () => void;
}) {
  return (
    <Button
      size="small"
      variant="outlined"
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onReset();
      }}
      onFocus={(event) => event.stopPropagation()}
      sx={{ ml: 'auto', mr: 1, flexShrink: 0 }}
    >
      {resetting && <CircularProgress size={14} sx={{ mr: 1 }} />}
      {resetting ? 'Resetting…' : 'Reset all'}
    </Button>
  );
}

interface Props {
  run: IRunEntry;
  onClose: () => void;
  onResetSuccess?: () => void;
}

export default function ErrorSummaryDialog({ run, onClose, onResetSuccess }: Props) {
  const errorSummaryAsync = useWorkflowErrorSummaryAsync(run.project_id, run.id);
  const summary =
    errorSummaryAsync.status === 'SUCCESS' || errorSummaryAsync.status === 'REFRESH'
      ? errorSummaryAsync.data
      : undefined;
  const [resetting, setResetting] = useState<IFailedTasksFilter | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  const isResetting = (filter: IFailedTasksFilter) =>
    resetting?.errorType === filter.errorType && resetting.errorMessage === filter.errorMessage;

  const reset = (filter: IFailedTasksFilter) => {
    const label = filter.errorMessage === undefined ? filter.errorType : `${filter.errorType} · ${filter.errorMessage}`;
    setResetting(filter);
    bulkResetFailedTasks(run.project_id, run.id, filter)
      .then((result) => {
        setResetMessage(
          result.recovered_count > 0
            ? `Reset ${result.recovered_count} failed task(s) with ${label}`
            : `No failed tasks with ${label} to reset`,
        );
        fetchWorkflowErrorSummary(run.project_id, run.id);
        onResetSuccess?.();
      })
      .catch((err) => {
        console.error('Reset failed tasks failed:', err);
        setResetMessage('Reset failed');
      })
      .finally(() => setResetting(null));
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Error summary — {run.name}</DialogTitle>
      <DialogContent>
        {['INIT', 'PENDING'].includes(errorSummaryAsync.status) && <LinearProgress />}
        {errorSummaryAsync.status === 'ERROR' && <Alert severity="error">Could not load the error summary</Alert>}
        {summary && (
          <>
            <Typography gutterBottom>
              {summary.total_failed_tasks} failed {summary.total_failed_tasks === 1 ? 'task' : 'tasks'}
            </Typography>
            {summary.errors.map((error) => {
              const typeFilter = { errorType: error.error_type };
              const variants = error.variants ?? [];
              return (
                <Accordion key={error.error_type}>
                  <AccordionSummary component="div" expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                    <span
                      css={css`
                        font-weight: 600;
                      `}
                    >
                      {error.error_type}
                    </span>
                    <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>
                      — {taskCount(error.count)}
                    </Typography>
                    <ResetAllButton
                      resetting={isResetting(typeFilter)}
                      disabled={resetting !== null}
                      onReset={() => reset(typeFilter)}
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    {variants.length <= 1 ? (
                      <FailureSample sample={variants[0] ?? error} />
                    ) : (
                      variants.map((variant) => {
                        const variantFilter = { errorType: error.error_type, errorMessage: variant.message };
                        return (
                          <Accordion key={variant.message} disableGutters>
                            <AccordionSummary component="div" expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                              <Typography
                                component="span"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.8rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  minWidth: 0,
                                }}
                                title={variant.message}
                              >
                                {variant.message || '(no message)'}
                              </Typography>
                              <Typography component="span" color="text.secondary" sx={{ ml: 1, flexShrink: 0 }}>
                                — {taskCount(variant.count)}
                              </Typography>
                              <ResetAllButton
                                resetting={isResetting(variantFilter)}
                                disabled={resetting !== null}
                                onReset={() => reset(variantFilter)}
                              />
                            </AccordionSummary>
                            <AccordionDetails>
                              <FailureSample sample={variant} />
                            </AccordionDetails>
                          </Accordion>
                        );
                      })
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
            <Box sx={{ mt: 2 }}>
              <ExpandableDetails
                summary="Raw JSON"
                contentPlaceholder={null}
                copyJson={summary}
                renderContent={() => <JsonViewer json={summary} />}
              />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button component={RouterLink} to={`/projects/${run.project_id}/workflows/${run.id}/details`}>
          Open details
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      <Snackbar
        open={resetMessage !== null}
        autoHideDuration={4000}
        onClose={() => setResetMessage(null)}
        message={resetMessage}
      />
    </Dialog>
  );
}
